import { Component, ElementRef, EventEmitter, Input, OnInit, Output, signal, ViewChild, Renderer2, AfterViewInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-profession',
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './profession.component.html',
  styleUrl: './profession.component.css'
})
export class ProfessionComponent implements OnInit, AfterViewInit, OnDestroy {

  codpros: any[] = [];
  form: FormGroup;
  isValid = false;
  @Input() disabled = false;
  @Input() resetTimer = false;
  @Input() defaultValue: any = null;
  @Input() label = '¿Cuál es tu profesión?';
  @Input() timeoutSeconds = 8; // Cambiado de timerMs a timeoutSeconds
  @Output() valueState = new EventEmitter<number | null>();
  @ViewChild('codpro') codpro!: ElementRef;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  // Signals para control de timeout
  offTimeOut = signal(false);
  hasWritten = signal(false);
  hasSelected = signal(false);
  private countdownTimer: any;
  private viewInitialized = signal(false);

  filterTimeout: any;
  selectedCodpro: any = null;
  filteredCodpros: any[] = [];

  constructor(
    private listService: ListsService,
    private renderer: Renderer2
  ) {
    this.form = new FormGroup({
      codpro: new FormControl('', [Validators.required]),
    });

    // Escuchar cambios del formulario
    this.form.valueChanges.subscribe((value) => {
      this.hasWritten.set(true);
      
      // Si hay un valor válido y se ha seleccionado, limpiar timeout
      if (this.form.valid && value.codpro && this.hasSelected()) {
        this.clearCountdown();
        this.offTimeOut.set(false);
        this.getStyleEnabled();
      }
    });
  }

  ngOnInit(): void {
    // Iniciar countdown siempre al inicio
    this.startCountdown();

    this.listService.getCodpro().subscribe({
      next: (response: any) => {
        this.codpros = response;
        this.setProfessionDefaultValue(this.defaultValue)
      },
    });

    if(this.disabled) {
      this.form.get('codpro')!.disable();
      this.getStyleTextDisabled();
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
  }

  ngOnChanges(): void {
    if(this.resetTimer) {
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.startCountdown();
    }

    if(this.defaultValue) {
      this.valueState.emit(this.defaultValue);
      this.hasSelected.set(true);
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.getStyleEnabled();
    }

    if(this.disabled) {
      this.form.get('codpro')!.disable();
      this.getStyleTextDisabled();
    } else {
      this.form.get('codpro')!.enable();
      this.getStyleEnabled();
    }
  }

  private applyInitialStyles() {
    if (this.disabled) {
      this.getStyleTextDisabled();
    } else {
      this.getStyleEnabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  // Métodos para el control de timeout y estilos
  startCountdown() {
    this.clearCountdown();
    
    this.countdownTimer = setTimeout(() => {
      
      if (!this.hasSelected() || !this.form.valid) {
        this.offTimeOut.set(true);
        this.getStyleError();
      }
    }, this.timeoutSeconds * 1000);
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }

  setProfessionDefaultValue(defaultValue: any) {
    this.form.get('codpro')!.setValue(defaultValue);
    if(this.defaultValue) {
      this.isValid = true;
      this.hasSelected.set(true);
    }
    const codproObj: any = this.codpros.filter((codpro: any) => codpro.value == defaultValue)[0];
    if(codproObj) {
      this.codpro.nativeElement.value = codproObj.label;
    }
  }

  filterProfession(event: any) {
    this.isValid = false;
    this.hasSelected.set(false); // Al filtrar, se resetea la selección
    
    const query = event.target.value.toLowerCase();
    if(this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    
    this.filteredCodpros = this.codpros.filter((codpro: any) => 
      codpro.label.toLowerCase().includes(query)
    );
  }

  setProfession(idCodpro: number, codpro: string) {
    this.form.get('codpro')!.setValue(codpro);
    this.isValid = true;
    this.selectedCodpro = codpro;
    this.valueState.emit(idCodpro);
    this.filteredCodpros = [];
    
    // Marcar como seleccionado y limpiar timeout
    this.hasSelected.set(true);
    this.clearCountdown();
    this.offTimeOut.set(false);
    this.getStyleEnabled();
  }

  esc(){
    this.filteredCodpros = [];
    this.form.get('codpro')!.setValue('');
    this.isValid = false;
    this.valueState.emit(null);
    this.hasSelected.set(false);
    
    // Reiniciar el countdown cuando se limpia
    this.startCountdown();
  }

  makeInvalid() {
    this.isValid = false;
    this.valueState.emit(null);
    this.hasSelected.set(false);
  }

  validateBlur() {
    setTimeout(() => {
      if(!this.isValid){
        this.esc()
      }
    }, 1000);
  }

  hasError = () => {
    const shouldShowError = this.offTimeOut() && (!this.hasSelected() || !this.form.valid);
    return shouldShowError;
  }

  ngOnDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.clearCountdown();
  }
}